import FontFaceObserver from 'fontfaceobserver';
import { markRaw } from 'vue';

// Text editing mixin for Vue.js
// Provides functionality for editing text in balloons and standalone text objects
export default {
    data() {
        return {
            // Text editing properties
            text_editing: {
                active: false,
                adjust_mode: false,
                text: '',
                font_size: 22,
                font_family: 'New Wild Words, Arial, sans-serif',
                font_style: 'normal',
                align: 'center',
                text_color: '#000000',
                text_stroke: '#FFFFFF',
                text_stroke_width: 0,
                line_height: 1,
                is_bold: false,
                is_italic: false,
            },
            current_editing_text_node_index: 0, // Add this line
            _is_line_height_update_scheduled: false, // Flag for line height update
        };
    },

    mounted() {

        // Lista completa de fuentes reales
        const font_families = [
            // Basic
            'Wild Words', 'Clementine',
            
            // Extended
            'New Wild Words', 'Anime Ace', 'Anime Ace 3', 'Ames', 'CC Astrocity', 'Joe Mad',
            
            // Chinese
            'Yozai',
            
            // Japanese
            'GenEiAntique', 'GenEiAntiqueP', 'GenEiKoburi', 'GenEiMGothic',
            
            // Korean
            'KakaoBigSans', 'KakaoSmallSans', 'SinchonRhapsody', 'EunglwolYakdahyun',
            
            // Other
            'Kalam'
        ];
          
        font_families.forEach(font => {
            const loader = new FontFaceObserver(font);
            loader.load().then(() => {
                //console.log(`Font ${font} loaded successfully`);
            }).catch(err => {
                console.warn(`Font ${font} failed to load:`, err);
            });
        });

    },
    
    methods: {

        initialize_text_editing_from_defaults() {
            if (this.preferences.text_defaults) {
                this.text_editing.font_size = this.preferences.text_defaults.font_size;
                this.text_editing.font_family = this.preferences.text_defaults.font_family;
                this.text_editing.font_style = this.preferences.text_defaults.font_style;
                this.text_editing.align = this.preferences.text_defaults.align;
                this.text_editing.text_color = this.preferences.text_defaults.text_color;
                this.text_editing.text_stroke = this.preferences.text_defaults.text_stroke;
                this.text_editing.text_stroke_width = this.preferences.text_defaults.text_stroke_width;
                this.text_editing.line_height = this.preferences.text_defaults.line_height;
                this.text_editing.is_bold = this.preferences.text_defaults.is_bold;
                this.text_editing.is_italic = this.preferences.text_defaults.is_italic;
            }
        },

        get_active_balloon_text_node() {
            if (!this.selected_object) return null;
            
            // Check for standalone text first
            if (this.selected_object.getAttr('object_type') === 'standalone_text') {
                return this.selected_object.findOne('Text');
            }
            
            // Then check for balloon
            if (this.selected_object.getAttr('object_type') !== 'balloon') return null;
            
            // Combined balloon
            if (this.selected_object.texts && Array.isArray(this.selected_object.texts)) {
                const idx = this.active_text_index_in_merged_balloon ?? 0;
                return this.selected_object.texts[idx] || null;
            }
            // Standard balloon
            return this.selected_object.text || this.selected_object.findOne?.('Text') || null;
        },
        
        async create_standalone_text(x, y, options = {}) {
            if (!this.ui_layer) {
                console.error('UI layer not available');
                return null;
            }

            // Determine target panel for layer validation
            let target_panel = this.selected_panel;
            if (!target_panel || target_panel.getAttr('is_global')) {
                target_panel = this.panels.find(p => p.getAttr('is_global'));
            }

            // Check premium limit for layers
            if (target_panel) {
                const current_layer_count = this.get_panel_layer_count(target_panel);
                if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                    return null;
                }
            }

            // Create text group - RAW OBJECT
            const text_group_raw = new Konva.Group({
                x: x,
                y: y,
                draggable: true,
                name: 'standalone_text',
                id: 'standalone_text_' + Date.now(),
            });

            // Mark the Konva object as raw to prevent Vue reactivity
            const text_group = markRaw(text_group_raw);

            // Create invisible text box for transformations (like balloons)
            const text_box = new Konva.Rect({
                x: -75, // Centered relative to group
                y: -25,
                width: 250,
                height: 150,
                fill: 'transparent',
                stroke: 'transparent',
                name: 'text_box',
                listening: false,
            });

            // Create text content with proper default text
            const default_text = 'Double-click to edit';
            const text_content = options.text !== undefined ? options.text : default_text;
            
            const text_node = new Konva.Text({
                text: text_content,
                fontSize: options.font_size || this.preferences.text_defaults.font_size,
                fontFamily: options.font_family || this.preferences.text_defaults.font_family,
                fill: options.text_color || this.preferences.text_defaults.text_color,
                stroke: options.text_stroke || this.preferences.text_defaults.text_stroke,
                strokeWidth: options.text_stroke_width !== undefined ? options.text_stroke_width : this.preferences.text_defaults.text_stroke_width,
                align: options.align || this.preferences.text_defaults.align,
                lineHeight: options.line_height || this.preferences.text_defaults.line_height,
                fillAfterStrokeEnabled: true,
                x: 0,
                y: 0,
                name: 'text_content',
                wrap: 'word',
                ellipsis: false,
                listening: true
            });

            // Add elements to group
            text_group.add(text_box);
            text_group.add(text_node);
            // Set attributes
            text_group.setAttr('object_type', 'standalone_text');
            text_group.setAttr('custom_text_position', true);
            text_group.setAttr('custom_text_box_x', text_box.x());
            text_group.setAttr('custom_text_box_y', text_box.y());
            text_group.setAttr('custom_text_box_width', text_box.width());
            text_group.setAttr('custom_text_box_height', text_box.height());
            
            // Set default blend mode and opacity for proper layer controls
            text_group.globalCompositeOperation('normal');
            text_group.opacity(1);

            // Add references for easier access
            text_group.text_box = text_box;
            text_group.text = text_node;

            // Add transform event listener to recenter text on resize/transform
            text_group.on('transform', () => {
                this.recenter_standalone_text(text_group);
            });

            // Add to target panel and set sequential name
            if (target_panel && target_panel.content_group) {
                const layer_name = this.get_next_layer_name(target_panel);
                text_group.setAttr('layer_name', layer_name);
                target_panel.content_group.add(text_group);
            } 
            else {
                // Fallback to ui_layer if no panel available
                text_group.setAttr('layer_name', `Text ${Date.now()}`);
                this.ui_layer.add(text_group);
            }

            // Set up rotation center offset for proper rotation behavior
            const center_x = text_box.x() + text_box.width() / 2;
            const center_y = text_box.y() + text_box.height() / 2;
            
            // Set offset to center for proper rotation
            text_group.offsetX(center_x);
            text_group.offsetY(center_y);
            
            // Adjust position to compensate for the offset change
            text_group.x(text_group.x() + center_x);
            text_group.y(text_group.y() + center_y);

            // Center the text within its box
            this.recenter_standalone_text(text_group);

            this.redraw();
            return text_group;
        },

        recenter_standalone_text(text_group, preserve_text_rotation = true) {
            if (!text_group || text_group.getAttr('object_type') !== 'standalone_text') return;

            const text_node = text_group.findOne('Text');
            const text_box = text_group.findOne('.text_box') || text_group.text_box;
            if (!text_node || !text_box) return;

            const group_scale_x = text_group.scaleX() || 1;
            const group_scale_y = text_group.scaleY() || 1;
            if (group_scale_x === 0 || group_scale_y === 0) return;

            const inverse_scale_x = 1 / group_scale_x;
            const inverse_scale_y = 1 / group_scale_y;

            // Apply inverse scale to text to maintain consistent appearance
            text_node.scaleX(inverse_scale_x);
            text_node.scaleY(inverse_scale_y);

            if (!preserve_text_rotation) {
                text_node.rotation(-text_group.rotation());
            }

            // Get text box bounds (use custom box if available)
            let box_for_text;
            if (text_group.getAttr('custom_text_position') === true &&
                typeof text_group.getAttr('custom_text_box_width') === 'number' && 
                text_group.getAttr('custom_text_box_width') > 0) {
                box_for_text = {
                    x: text_group.getAttr('custom_text_box_x'),
                    y: text_group.getAttr('custom_text_box_y'),
                    width: text_group.getAttr('custom_text_box_width'),
                    height: text_group.getAttr('custom_text_box_height')
                };
            } else {
                box_for_text = text_box.getClientRect({ relativeTo: text_group });
            }

            // Calculate target width (use same padding as balloons)
            const padding_allowance = 0.80;
            const target_visual_width = box_for_text.width * padding_allowance;
            const required_unscaled_width = target_visual_width / inverse_scale_x;

            text_node.width(required_unscaled_width);
            const text_height = text_node.height();

            // Check if text fits vertically
            const available_height = box_for_text.height * padding_allowance;
            const scaled_text_height = text_height * inverse_scale_y;

            text_node.visible(true);
            
            if (scaled_text_height > available_height) {
                text_node.visible(false);
            }

            // Center text in box
            const center_x_of_box = box_for_text.x + box_for_text.width / 2;
            const center_y_of_box = box_for_text.y + box_for_text.height / 2;

            text_node.x(center_x_of_box);
            text_node.y(center_y_of_box);
            text_node.offsetX(required_unscaled_width / 2);
            text_node.offsetY(text_height / 2);

            const layer = text_group.getLayer();
            if (layer) {
                layer.batchDraw();
            }
        },
        
        update_text_content() {
            if (!this.text_editing.active) return;
            const text_node = this.get_active_balloon_text_node();
            if (!text_node) return;
            if (this.text_editing.text !== null && this.text_editing.text !== undefined) {
                text_node.text(this.text_editing.text);
                
                // Recenter text based on object type
                if (this.selected_object.getAttr('object_type') === 'standalone_text') {
                    this.recenter_standalone_text(this.selected_object);
                } 
                else if (typeof this.recenter_balloon_text === 'function') {
                    this.recenter_balloon_text(this.selected_object);
                }
                this.redraw();
            }
        },

        update_text_font() {
            if (!this.text_editing.active) return;

            const text_node = this.get_active_balloon_text_node();
            if (!text_node) return;

            let changed = false;
            if (this.text_editing.font_family) {
                text_node.fontFamily(this.text_editing.font_family);
                changed = true;
            }

            const font_size = parseInt(this.text_editing.font_size);
            if (!isNaN(font_size) && font_size > 0) {
                text_node.fontSize(font_size);
                changed = true;
            }
            
            if (changed) {
                // Recenter text based on object type
                if (this.selected_object.getAttr('object_type') === 'standalone_text') {
                    this.recenter_standalone_text(this.selected_object);
                } else if (typeof this.recenter_balloon_text === 'function') {
                    this.recenter_balloon_text(this.selected_object);
                }
                this.redraw();
                this.push_to_history('update_text_font');
            }
        },

        update_text_align() {
            if (!this.text_editing.active) return;
            const text_node = this.get_active_balloon_text_node();
            if (!text_node) return;
            if (this.text_editing.align) {
                text_node.align(this.text_editing.align);
                
                // Recenter text based on object type
                if (this.selected_object.getAttr('object_type') === 'standalone_text') {
                    this.recenter_standalone_text(this.selected_object);
                } else if (typeof this.recenter_balloon_text === 'function') {
                    this.recenter_balloon_text(this.selected_object);
                }
                this.redraw();
                this.push_to_history('update_text_align');
            }
        },

        update_text_color() {
            if (!this.text_editing.active || this._is_color_update_scheduled) return;
            this._is_color_update_scheduled = true;
            requestAnimationFrame(() => {
                const text_node = this.get_active_balloon_text_node();
                if (!this.text_editing.active || !text_node) {
                    this._is_color_update_scheduled = false;
                    return;
                }
                const default_text_color = this.preferences.text_defaults.text_color;
                const color = this.text_editing.text_color;
                if (color && color.startsWith('#')) {
                    text_node.fill(color);
                } else {
                    text_node.fill(default_text_color);
                }
                this.redraw();
                this._is_color_update_scheduled = false;
            });
        },


        update_text_outline() {
            if (!this.text_editing.active || this._is_outline_update_scheduled) return;
            this._is_outline_update_scheduled = true;
            requestAnimationFrame(() => {
                const text_node = this.get_active_balloon_text_node();
                if (!this.text_editing.active || !text_node) {
                    this._is_outline_update_scheduled = false;
                    return;
                }
                const default_text_stroke = '#FFFFFF';
                const color = this.text_editing.text_stroke;
                const width = parseFloat(this.text_editing.text_stroke_width);
                
                // Enable exterior stroke rendering
                text_node.fillAfterStrokeEnabled(true);
                
                if (color && color.startsWith('#')) {
                    text_node.stroke(color);
                } else {
                    text_node.stroke(default_text_stroke);
                }
                if (width !== null && width !== undefined && !isNaN(width)) {
                    text_node.strokeWidth(width);
                } else {
                    text_node.strokeWidth(0);
                }
                this.redraw();
                this._is_outline_update_scheduled = false;
            });
        },

        update_text_line_height() {
            if (!this.text_editing.active || this._is_line_height_update_scheduled) return;
            
            this._is_line_height_update_scheduled = true;
            requestAnimationFrame(() => {
                const text_node = this.get_active_balloon_text_node();
                if (!this.text_editing.active || !text_node) {
                    this._is_line_height_update_scheduled = false;
                    return;
                }
                
                const line_height = parseFloat(this.text_editing.line_height);
                if (!isNaN(line_height) && line_height > 0) {
                    text_node.lineHeight(line_height);
                    
                    // Recenter text based on object type
                    if (this.selected_object.getAttr('object_type') === 'standalone_text') {
                        this.recenter_standalone_text(this.selected_object);
                    } else if (typeof this.recenter_balloon_text === 'function') {
                        this.recenter_balloon_text(this.selected_object);
                    }
                    this.redraw();
                }
                this._is_line_height_update_scheduled = false;
            });
        },

        toggle_text_style(style) {
            if (!this.text_editing.active) return;
            const text_node = this.get_active_balloon_text_node();
            if (!text_node) return;
            if (style === 'bold') {
                this.text_editing.is_bold = !this.text_editing.is_bold;
            } else if (style === 'italic') {
                this.text_editing.is_italic = !this.text_editing.is_italic;
            }
            text_node.fontStyle(this.get_combined_text_style());
            text_node.getLayer()?.batchDraw();
            this.push_to_history('update_text_style');
        },
        
        get_combined_text_style() {
            let style = 'normal';
            
            if (this.text_editing.is_bold && this.text_editing.is_italic) {
                style = 'bold italic';
            } else if (this.text_editing.is_bold) {
                style = 'bold';
            } else if (this.text_editing.is_italic) {
                style = 'italic';
            }
            
            return style;
        },

        enable_exterior_stroke_for_existing_texts() {
            // Enable fillAfterStrokeEnabled for all existing text nodes
            this.panels.forEach(panel => {
                if (panel.content_group) {
                    // Handle standalone texts
                    panel.content_group.find('.standalone_text').forEach(text_group => {
                        const text_node = text_group.findOne('Text');
                        if (text_node) {
                            text_node.fillAfterStrokeEnabled(true);
                        }
                    });
                    
                    // Handle balloon texts
                    panel.content_group.find('.panel_balloon').forEach(balloon_group => {
                        // Single text balloon
                        if (balloon_group.text) {
                            balloon_group.text.fillAfterStrokeEnabled(true);
                        }
                        
                        // Multiple texts (merged balloons)
                        if (balloon_group.texts && Array.isArray(balloon_group.texts)) {
                            balloon_group.texts.forEach(text_node => {
                                if (text_node) {
                                    text_node.fillAfterStrokeEnabled(true);
                                }
                            });
                        }
                    });
                }
            });
            
            this.redraw();
        }
    }
};